import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Search } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { useFeatureFlagsList } from "./use-feature-flags-list";
import { FeatureFlagsList } from "./FeatureFlagsList";
import { FeatureFlagsListEmpty } from "./FeatureFlagsListEmpty";
import { FeatureFlagsListSkeleton } from "./FeatureFlagsListSkeleton";
import { RefreshFeatureFlagsButton } from "./RefreshFeatureFlagsButton";

export function FeatureFlagsPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  const { data: items, isLoading, isError } = useFeatureFlagsList();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!items) return [];
    const needle = search.trim().toLowerCase();
    if (needle.length === 0) return items;
    return items.filter(
      (f) => f.id.toLowerCase().includes(needle) || f.description.toLowerCase().includes(needle),
    );
  }, [items, search]);

  return (
    <div data-testid="feature-flags-screen" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="brand-eyebrow">{t("featureFlags.eyebrow")}</p>
          <h1 className="text-4xl">{t("featureFlags.title")}</h1>
          <p className="font-sans text-base font-normal text-muted-foreground">
            {t("featureFlags.subtitle")}
          </p>
        </div>
        {canEdit && <RefreshFeatureFlagsButton />}
      </header>

      {!canEdit && (
        <div
          data-testid="feature-flags-readonly-banner"
          className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
        >
          <Info className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <p className="font-sans text-sm font-normal text-muted-foreground">
            {t("featureFlags.readOnlyBanner")}
          </p>
        </div>
      )}

      <div className="relative max-w-md">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          data-testid="feature-flags-search-input"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("featureFlags.search.placeholder")}
          aria-label={t("featureFlags.search.label")}
          className="pl-9"
        />
      </div>

      {isLoading && <FeatureFlagsListSkeleton />}

      {isError && (
        <div
          data-testid="feature-flags-list-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("featureFlags.list.error")}
          </p>
        </div>
      )}

      {items && items.length === 0 && <FeatureFlagsListEmpty />}

      {items && items.length > 0 && filtered.length === 0 && (
        <div
          data-testid="feature-flags-list-no-matches"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {t("featureFlags.search.noMatchesTitle")}
          </p>
          <p className="mt-1 font-sans text-sm font-normal text-muted-foreground">
            {t("featureFlags.search.noMatchesBody")}
          </p>
        </div>
      )}

      {filtered.length > 0 && <FeatureFlagsList items={filtered} />}
    </div>
  );
}
